import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BookOpen, Compass, Target, Package, Users, Layers, ClipboardCheck,
  UserPlus, KeyRound, Bot, Mic, Trophy, Flag, Award, History, BarChart3,
  CreditCard, PlayCircle, CalendarClock, GraduationCap, Swords, Medal, CalendarDays, ListChecks, type LucideIcon,
} from "lucide-react";
import type { PageType, User } from "../types";
import { getGuideSectionsForProfile } from "../lib/client-guide-content";
import { encadrantRoleFor } from "../lib/encadrant";
import { useGuide, HELP_SCROLL_KEY } from "../components/guide/InteractiveGuide";
import { getWalkthroughByGuideSection } from "../components/guide/walkthroughs";

interface HelpPageProps {
  user: User;
  onNavigate: (page: PageType) => void;
}

// Résolution des noms d'icônes (stockés en clair dans le module de contenu)
const ICONS: Record<string, LucideIcon> = {
  Compass, Target, Package, Users, Layers, ClipboardCheck, UserPlus,
  KeyRound, Bot, Mic, Trophy, Flag, Award, History, BarChart3, CreditCard,
  CalendarClock, GraduationCap, Swords, Medal, CalendarDays, ListChecks,
};

/**
 * Rendu markdown léger, aligné sur le renderer de CoachPage.tsx :
 * échappe le HTML puis convertit titres / gras / italique / listes.
 * (Contenu 100 % maîtrisé côté code — pas d'entrée utilisateur.)
 */
function renderMarkdown(md: string) {
  const html = md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 pl-3 my-2 italic" style="border-color:#A6D3F9;color:#5A6788">$1</blockquote>')
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-4 mb-1" style="color:#384872">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-5 mb-2" style="color:#384872;font-family:Sora,sans-serif">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#384872">$1</strong>')
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-5 list-decimal text-sm" style="color:#4A5878">$1</li>')
    .replace(/^ {2,}- (.+)$/gm, '<li class="ml-8 list-disc text-sm" style="color:#4A5878">$1</li>')
    .replace(/^- (.+)$/gm, '<li class="ml-5 list-disc text-sm" style="color:#4A5878">$1</li>')
    .replace(/\n{2,}/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");
  return <div className="prose prose-sm max-w-none leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
}

export function HelpPage({ user }: HelpPageProps) {
  const { t, i18n } = useTranslation();
  const guide = useGuide();
  // Guide conditionné par la vue (Play/Pilot) pour les profils qui ont le switcher :
  // un opérationnel ne voit pas le détail des fonctions manager. Vue mémorisée à
  // l'ouverture ; un toggle permet à l'encadrant de consulter l'autre espace.
  const hasSwitcher = !!encadrantRoleFor(user.profileType);
  const [space, setSpace] = useState<"play" | "pilot" | null>(() => {
    if (!hasSwitcher) return null;
    let s: string | null = null;
    try { s = localStorage.getItem("bizcoach_help_scope"); } catch { /* noop */ }
    return s === "play" ? "play" : "pilot";
  });
  const sections = useMemo(
    () => getGuideSectionsForProfile(user.profileType, i18n.language, space),
    [user.profileType, i18n.language, space],
  );
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");
  // Si la section active disparaît (changement d'espace), on repositionne sur la 1re.
  useEffect(() => {
    if (sections.length && !sections.some((s) => s.id === activeId)) setActiveId(sections[0].id);
  }, [sections, activeId]);

  const scrollToSection = (id: string) => {
    setActiveId(id);
    const el = document.getElementById(`guide-section-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Défilement auto vers une section demandée par une visite guidée (bouton « En savoir plus »).
  useEffect(() => {
    let target: string | null = null;
    try { target = localStorage.getItem(HELP_SCROLL_KEY); localStorage.removeItem(HELP_SCROLL_KEY); } catch { /* noop */ }
    if (!target) return;
    const id = target;
    // Laisse le temps au rendu, puis défile.
    const tid = setTimeout(() => {
      const el = document.getElementById(`guide-section-${id}`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); setActiveId(id); }
    }, 150);
    return () => clearTimeout(tid);
  }, []);

  return (
    <div className="container mx-auto px-4 sm:px-6 py-6">
      {/* En-tête */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: "#A6D3F9" }}>
          <BookOpen className="h-6 w-6" style={{ color: "#384872" }} />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
            {t("clientGuide.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("clientGuide.subtitle")}</p>
        </div>
        {/* Toggle Play/Pilot : l'encadrant peut consulter le guide de l'autre espace. */}
        {hasSwitcher && (
          <div className="hidden sm:flex items-center rounded-full p-1 gap-1" style={{ background: "#EEF1F7" }}>
            <button onClick={() => setSpace("play")} className="rounded-full px-3 py-1.5 text-xs font-bold transition"
              style={space === "play" ? { background: "#1E7A3C", color: "#fff" } : { color: "#5A6076" }}>{t("nav.viewPlay")}</button>
            <button onClick={() => setSpace("pilot")} className="rounded-full px-3 py-1.5 text-xs font-bold transition"
              style={space === "pilot" ? { background: "#5A4BBE", color: "#fff" } : { color: "#5A6076" }}>{t("nav.viewSetup")}</button>
          </div>
        )}
      </div>

      {sections.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("clientGuide.emptyState")}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* Sommaire */}
          <aside className="lg:sticky lg:top-20 lg:self-start">
            <nav aria-label={t("clientGuide.tocTitle")} className="rounded-lg border bg-white p-2">
              <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("clientGuide.tocTitle")}
              </p>
              <ul className="space-y-0.5 max-h-[60vh] lg:max-h-none overflow-auto">
                {sections.map((s) => {
                  const Icon = ICONS[s.icon] ?? BookOpen;
                  const isActive = s.id === activeId;
                  return (
                    <li key={s.id}>
                      <button
                        onClick={() => scrollToSection(s.id)}
                        className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors ${
                          isActive ? "bg-muted font-medium" : "hover:bg-muted/60"
                        }`}
                        style={isActive ? { color: "#384872" } : undefined}
                      >
                        <Icon className="h-4 w-4 shrink-0" style={{ color: "#384872" }} />
                        <span>{s.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* Contenu */}
          <div className="min-w-0 space-y-4">
            {sections.map((s) => {
              const Icon = ICONS[s.icon] ?? BookOpen;
              const walkthrough = getWalkthroughByGuideSection(s.id, space);
              return (
                <section
                  key={s.id}
                  id={`guide-section-${s.id}`}
                  className="scroll-mt-20 rounded-lg border bg-white p-5 sm:p-6"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className="h-5 w-5 shrink-0" style={{ color: "#384872" }} />
                      <h2 className="text-xl font-bold truncate" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
                        {s.title}
                      </h2>
                    </div>
                    {walkthrough && guide && (
                      <button
                        onClick={() => guide.runWalkthrough(walkthrough.id)}
                        className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white transition-transform hover:scale-105"
                        style={{ backgroundColor: "#384872" }}
                      >
                        <PlayCircle className="h-4 w-4" />
                        {t("guide.startWalkthrough")}
                      </button>
                    )}
                  </div>
                  {renderMarkdown(s.body)}
                </section>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
